import { createContext, PropsWithChildren, useContext, useEffect } from 'react'
import { History } from 'history'
import { useDialogControllers } from '@machobear/design-system'
import { useLocation } from 'react-router-dom'
import { ShareListingDialogs } from '@/features/Listing/buttons/share/share-modal'
import ListingExternalEditNoticeModal from '@/components/listing-external-edit-notice-modal'
import VerifyCodeModal from '@/pages/Listing/Verify/verify-code-modal'
import VerificationSuccessModal from '@/pages/Listing/Verify/verification-success-modal'
import MaxAttemptsModal from '@/pages/Listing/Verify/max-attempts-modal'
import { ListingSponsorModal } from './listing-sponsor-modal'
import { ListingHiddenModal } from './listing-hidden-modal'
import { useListingData } from '../context/listing-data'

const ListringDashboardModalsContext = createContext<{ showModal: any }>({
  showModal: () => {}
})

interface Props extends PropsWithChildren {
  history: History
}

export const ListingDashboardModalsProvider = ({ children, history }: Props) => {
  const location = useLocation()

  const { listingId, externalInfo } = useListingData()

  const { visible, showModal, closeModal } = useDialogControllers({
    noBillingUser: false,
    sponsoredListing: false,
    nextSteps: false,
    checkoutDialog: false,
    editPaymentDialog: false,
    mainDialog: false,
    emailDialog: false,
    phoneDialog: false,
    craigslistDialog: false,
    kijijiDialog: false,
    facebookDialog: false,
    verifyCode: false,
    maxAttempts: false,
    verificationSuccess: false,
    editListingOutsideModal: false,
    listingHiddenModal: false
  })

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    if (queryParams.has('verified')) {
      showModal('verificationSuccess')()
    }
  }, [location.search])

  return (
    <ListringDashboardModalsContext.Provider value={{ showModal }}>
      {children}

      <ListingSponsorModal
        history={history}
        listingId={listingId}
        visible={visible}
        showModal={showModal}
        closeModal={closeModal}
      />

      <ShareListingDialogs
        listingId={listingId}
        visible={visible}
        showModal={showModal}
        closeModal={closeModal}
      />
      <VerifyCodeModal
        listingId={listingId}
        history={history}
        showModal={showModal}
        open={visible.verifyCode}
        onClose={closeModal('verifyCode')}
      />
      <MaxAttemptsModal
        listingId={listingId}
        history={history}
        open={visible.maxAttempts}
        onClose={closeModal('maxAttempts')}
      />
      <VerificationSuccessModal
        listingId={listingId}
        history={history}
        open={visible.verificationSuccess}
        onClose={closeModal('verificationSuccess')}
      />
      <ListingExternalEditNoticeModal
        visible={visible.editListingOutsideModal}
        closeModal={closeModal('editListingOutsideModal')}
        externalInfo={externalInfo}
      />
      <ListingHiddenModal
        visible={visible.listingHiddenModal}
        onClose={closeModal('listingHiddenModal')}
      />
    </ListringDashboardModalsContext.Provider>
  )
}

export const useListingDashboardModals = () => useContext(ListringDashboardModalsContext)
